import React from 'react';
import { Link } from 'react-router-dom';
import slugify from '../../utils/slugify';
import { ArrowRight, Star, Sparkles, ExternalLink, Calendar } from 'lucide-react';

interface ToolCardProps {
  icon: string;
  title: string;
  description: string;
  isFree?: boolean;
  tags?: string[];
  category?: string;
  popularity?: number;
  dateAdded?: string;
  showPopularity?: boolean;
}

const ToolCard: React.FC<ToolCardProps> = ({
  icon,
  title,
  description,
  isFree,
  tags = [],
  category = 'general',
  popularity = 50, // Default value
  dateAdded,
  showPopularity = false,
}) => {
  const link = `/tools/${slugify(category)}/${slugify(title)}`;

  // Calculate star rating from popularity (0-100 to 0-5 stars)
  const starRating = Math.round((popularity / 20) * 10) / 10;

  return (
    <Link
      to={link}
      className="group relative block bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 hover:-translate-y-0.5 h-full flex flex-col"
    >
      {/* Free/Pro Badges */}
      {isFree && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            <span>Free</span>
          </div>
        </div>
      )}

      {!isFree && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
            <Star className="w-3 h-3" />
            <span>Pro</span>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex items-start gap-4 mb-4">
        {/* Icon Container */}
        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center text-white text-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>

        {/* Title & Description */}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 dark:text-white text-lg leading-tight mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors line-clamp-2">
            {title}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed line-clamp-2 mb-2">
            {description}
          </p>
          
          {/* Popularity Indicator - conditionally shown */}
          {showPopularity && (
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    className={`w-3 h-3 ${
                      star <= Math.round(starRating)
                        ? 'text-yellow-500 fill-yellow-500' 
                        : 'text-gray-300 dark:text-gray-600'
                    }`} 
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {starRating}/5
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tags Section */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="text-xs px-2.5 py-1 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700/50"
            >
              {tag}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              +{tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Footer with CTA */}
      <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-700/50">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide block">
              {category}
            </span>
            {dateAdded && (
              <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 mt-1">
                <Calendar className="w-3 h-3" />
                {new Date(dateAdded).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors">
            <span className="text-sm font-semibold">Details</span>
            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-blue-500/0 group-hover:from-purple-500/10 group-hover:to-blue-500/10 rounded-2xl transition-all duration-300 pointer-events-none"></div>
    </Link>
  );
};

export default ToolCard;